import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios'
import merge from 'lodash.merge'
import { IApiPath, RequestConfig } from '../types'
import { ApiHttpPaths } from './ApiHttpPaths'

export class AxiosAPI {
  private axiosInstance: AxiosInstance
  private sippAxiosInstance: AxiosInstance

  constructor() {
    this.apiSchema = ApiHttpPaths
    this.apiDomain = this.parseDomain()
    this.sippDomain = this.parseSippDomain()

    this.axiosInstance = axios.create()
    this.sippAxiosInstance = axios.create()
    this.setupInterceptors()
  }

  apiSchema: typeof ApiHttpPaths
  apiDomain = ''
  sippDomain = ''

  private parseDomain = () => {
    const apiUrl =
      (window.__ENV__ && window.__ENV__?.API_URL !== '__API_URL__' && window.__ENV__?.API_URL) ||
      process.env.REACT_APP_API_URL ||
      ''

    if (!apiUrl) {
      return window.location.origin
    }

    if (apiUrl.startsWith('/')) {
      return `${window.location.origin}${apiUrl}`
    }

    return apiUrl
  }

  private parseSippDomain = () => {
    const apiUrl =
      (window.__ENV__ &&
        window.__ENV__?.SIPP_EXPRESS_API_URL !== '__SIPP_EXPRESS_API_URL__' &&
        window.__ENV__?.SIPP_EXPRESS_API_URL) ||
      process.env.REACT_APP_SIPP_EXPRESS_API_URL ||
      ''

    return apiUrl
  }

  private setDefaultHeaders = (options: RequestConfig = {}): RequestConfig => {
    options.headers = {
      'Content-Type': 'application/json',
    }
    return options
  }

  private setupInterceptors = () => {
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (!error.response || error.code === 'ERR_NETWORK') {
          const customError = {
            ...error,
            message: 'Unable to connect to the server. Please check your connection and try again.',
            isConnectionError: true,
          }
          return Promise.reject(customError)
        }

        return Promise.reject(error)
      },
    )
  }

  public POST = (
    path: IApiPath,
    body: any,
    options: RequestConfig = {},
  ): Promise<AxiosResponse | AxiosError> => {
    const domain = path.useAltServer ? this.sippDomain : this.apiDomain
    const axiosInstance = path.useAltServer ? this.sippAxiosInstance : this.axiosInstance
    const defaultOptions: RequestConfig = this.setDefaultHeaders({
      timeout: 600000,
    })

    return axiosInstance.post(`${domain}${path.url}`, body, merge(defaultOptions, options))
  }

  public GET = (
    path: IApiPath,
    options: RequestConfig = {},
  ): Promise<AxiosResponse | AxiosError> => {
    const domain = this.apiDomain
    const defaultOptions: RequestConfig = this.setDefaultHeaders({
      timeout: 600000,
    })

    return this.axiosInstance.get(`${domain}${path.url}`, merge(defaultOptions, options))
  }

  public PUT = <T>(
    path: IApiPath,
    body: any,
    options: RequestConfig,
  ): Promise<AxiosResponse<T> | AxiosError> => {
    const domain = this.apiDomain
    const defaultOptions: RequestConfig = this.setDefaultHeaders({
      timeout: 600000,
    })

    return this.axiosInstance.put(`${domain}${path.url}`, body, merge(defaultOptions, options))
  }

  public DELETE = <T>(
    path: IApiPath,
    options: RequestConfig,
  ): Promise<AxiosResponse<T> | AxiosError> => {
    const domain = this.apiDomain
    const defaultOptions: RequestConfig = this.setDefaultHeaders({
      timeout: 600000,
    })

    return this.axiosInstance.delete(`${domain}${path.url}`, merge(defaultOptions, options))
  }
}
